import {
  AppstoreOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  LinkOutlined,
  ProjectOutlined,
  ScheduleOutlined,
  SettingOutlined,
  TableOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { App, ConfigProvider } from 'antd';
import React, { useEffect } from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  VersionCheck,
} from '@/components';
import {
  getMenus,
  currentUser as queryCurrentUser,
} from '@/services/ant-design-pro/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { setAppContext } from './utils/appContext';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

const AppContextHolder: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { message, notification, modal } = App.useApp();
  useEffect(() => {
    setAppContext({ message, notification, modal });
  }, [message, notification, modal]);
  return <>{children}</>;
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
/**
 * 将后端菜单数据转换为 Ant Design Pro 需要的格式
 */

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  menuData?: any[];
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };

  const fetchMenuData = async () => {
    try {
      const response = await getMenus({
        skipErrorHandler: true,
      });
      if (response.code === 0 && response.data) {
        return response.data;
      }
      return [];
    } catch (_error) {
      console.error('获取菜单数据失败', _error);
      return [];
    }
  };

  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    const menuData = await fetchMenuData();
    return {
      fetchUserInfo,
      currentUser,
      menuData,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const iconMap: Record<string, React.ReactNode> = {
    table: <TableOutlined />,
    'bar-chart': <BarChartOutlined />,
    user: <UserOutlined />,
    setting: <SettingOutlined />,
    'media-data': <DatabaseOutlined />,
    'log-data': <FileTextOutlined />,
    advert: <AppstoreOutlined />,
    plan: <ScheduleOutlined />,
    project: <ProjectOutlined />,
  };

  const loopMenuItem = (menus: any[]): any[] =>
    menus.map((item: any) => ({
      ...item,
      icon:
        typeof item.icon === 'string'
          ? iconMap[item.icon] || item.icon
          : item.icon,
      children: item.children ? loopMenuItem(item.children) : undefined,
    }));

  return {
    actionsRender: () => [],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content:
        window.location.hostname === 'test.ningzhishidai.com'
          ? '测试环境'
          : initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            {/* <span>OpenAPI 文档</span> */}
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 禁用菜单国际化，直接使用菜单的 name 字段
    menu: {
      locale: false,
    },
    // 动态生成菜单数据
    menuDataRender: (menuDataProps) => {
      // 如果从数据库获取了菜单数据，使用数据库的数据
      if (initialState?.menuData && initialState.menuData.length > 0) {
        return loopMenuItem(initialState.menuData);
      }
      // 否则使用默认的路由配置
      return loopMenuItem(menuDataProps);
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <ConfigProvider
          theme={
            initialState?.settings?.navTheme
              ? { algorithm: undefined }
              : undefined
          }
        >
          <App>
            <AppContextHolder>
              <VersionCheck />
              {children}
              {isDev && (
                <SettingDrawer
                  disableUrlParams
                  enableDarkTheme
                  settings={initialState?.settings}
                  onSettingChange={(settings) => {
                    setInitialState((preInitialState) => ({
                      ...preInitialState,
                      settings,
                    }));
                  }}
                />
              )}
            </AppContextHolder>
          </App>
        </ConfigProvider>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // 测试环境和生产环境统一配置
  ...(process.env.NODE_ENV === 'production'
    ? { baseURL: location.origin + '/admin/' }
    : {}),
  ...errorConfig,
};
